import './NewsPage.css';
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Newspaper, ExternalLink, Clock, TrendingUp, TrendingDown, 
  Bookmark, Share2, RefreshCw, Filter, ChevronDown, Check,
  Globe, Building2, Landmark, DollarSign, Bitcoin, Search,
  ChevronLeft, ChevronRight, Flame, AlertCircle, BookmarkCheck
} from 'lucide-react';
import { useUserPreferences } from '../context/UserPreferencesContext';

// Mock news data - replace with real API later
const mockNews = [
  {
    id: 1,
    title: 'Federal Reserve Signals Potential Rate Cuts in 2025 Amid Cooling Inflation',
    summary: 'The Federal Reserve indicated it may begin cutting interest rates in early 2025 as inflation continues to moderate toward its 2% target. Chair Powell emphasized a data-dependent approach while acknowledging significant progress in the fight against inflation.',
    content: `The Federal Reserve signaled Wednesday that it may begin cutting interest rates in early 2025, marking a potential shift in monetary policy as inflation continues to cool.

In his post-meeting press conference, Fed Chair Jerome Powell acknowledged "significant progress" in bringing down inflation, which has fallen from a peak of 9.1% in June 2022 to approximately 3.2% currently.

"We believe our policy rate is likely at its peak for this tightening cycle," Powell said. "The question now is how long to maintain this restrictive stance."

The Fed's updated economic projections suggest three quarter-point rate cuts in 2025, though Powell emphasized that future decisions would remain data-dependent.

Markets rallied on the news, with the S&P 500 jumping 1.4% and the 10-year Treasury yield falling to 4.2%. Rate-sensitive sectors like real estate and utilities led the gains.

Economists noted that the Fed faces a delicate balancing act between supporting economic growth and ensuring inflation doesn't reaccelerate.

"This is a pivotal moment," said chief economist Sarah Chen. "The Fed is threading the needle between premature easing and overtightening that could trigger a recession."`,
    source: 'Reuters',
    sourceUrl: 'https://www.reuters.com',
    category: 'economy',
    timestamp: '2 hours ago',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800',
    sentiment: 'bullish',
    tickers: ['SPY', 'QQQ', 'TLT'],
    breaking: true,
    readTime: '4 min read',
    regions: ['US', 'GLOBAL']
  },
  {
    id: 2,
    title: 'Bitcoin Surges Past $105,000 as Institutional Adoption Accelerates',
    summary: 'Bitcoin reached a new all-time high above $105,000 as major financial institutions continue to expand their cryptocurrency offerings and ETF inflows hit record levels.',
    content: `Bitcoin surged to a new all-time high above $105,000 on Thursday, driven by accelerating institutional adoption and record inflows into spot Bitcoin ETFs.

The world's largest cryptocurrency gained 8% in 24 hours, pushing its market capitalization above $2 trillion for the first time. The rally extended gains that have seen Bitcoin more than double since the approval of spot ETFs in January.

BlackRock's iShares Bitcoin Trust (IBIT) has accumulated over $25 billion in assets, making it one of the most successful ETF launches in history. Combined ETF inflows exceeded $1.5 billion this week alone.

"We're seeing a fundamental shift in how institutions view Bitcoin," said crypto analyst Michael Torres. "It's no longer a question of if, but how much allocation."

Major banks including Goldman Sachs and Morgan Stanley have expanded their crypto custody and trading services, while corporate treasuries continue to add Bitcoin to their balance sheets.

The surge has lifted the broader crypto market, with Ethereum gaining 6% and Solana up 12%. Total crypto market cap now exceeds $3.5 trillion.`,
    source: 'CoinDesk',
    sourceUrl: 'https://www.coindesk.com',
    category: 'crypto',
    timestamp: '3 hours ago',
    image: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=800',
    sentiment: 'bullish',
    tickers: ['BTC', 'ETH', 'COIN'],
    breaking: true,
    readTime: '3 min read',
    regions: ['GLOBAL', 'US', 'EU', 'JP', 'KR']
  },
  {
    id: 3,
    title: 'Apple Reports Record Q4 Earnings, Services Revenue Hits New High',
    summary: 'Apple exceeded Wall Street expectations with record fourth-quarter results, driven by strong iPhone sales and services revenue that topped $25 billion.',
    content: `Apple Inc. reported record fourth-quarter earnings Thursday, beating analyst expectations across all major metrics as iPhone sales and services revenue reached new highs.

The tech giant posted earnings per share of $2.18, above the $2.10 consensus estimate, on revenue of $94.9 billion. Services revenue hit a record $25.3 billion, up 16% year-over-year.

iPhone revenue came in at $48.7 billion, driven by strong demand for the iPhone 15 Pro models. CEO Tim Cook highlighted the "incredible customer response" to the company's AI features.

"We're seeing tremendous excitement around Apple Intelligence," Cook said on the earnings call. "This is just the beginning of what AI can do on our platform."

The company's gross margin expanded to 46.2%, reflecting the shift toward higher-margin services. Apple now has over 1 billion paid subscriptions across its services ecosystem.

Shares rose 4% in after-hours trading, pushing Apple's market cap closer to $3.5 trillion. The company also announced a $110 billion share buyback program.`,
    source: 'Bloomberg',
    sourceUrl: 'https://www.bloomberg.com',
    category: 'earnings',
    timestamp: '4 hours ago',
    image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800',
    sentiment: 'bullish',
    tickers: ['AAPL'],
    breaking: false,
    readTime: '3 min read',
    regions: ['US', 'GLOBAL']
  },
  {
    id: 4,
    title: 'Congress Passes Bipartisan Infrastructure Bill Worth $1.2 Trillion',
    summary: 'A landmark infrastructure bill cleared Congress with bipartisan support, allocating funds for roads, bridges, broadband, and clean energy projects.',
    content: `Congress passed a historic $1.2 trillion infrastructure bill Thursday with bipartisan support, marking the largest investment in American infrastructure in decades.

The legislation allocates $550 billion in new spending over five years, including $110 billion for roads and bridges, $66 billion for rail, $65 billion for broadband, and $7.5 billion for electric vehicle charging stations.

"This is a once-in-a-generation investment in our nation's future," said the Senate Majority Leader. "It will create millions of good-paying jobs and position America to compete in the 21st century."

The bill passed the Senate 69-30 and the House 228-206, with support from both parties. The President is expected to sign it into law next week.

Infrastructure stocks rallied on the news, with construction and materials companies leading gains. Caterpillar rose 5%, while United Rentals gained 7%.

Analysts estimate the bill could add 0.5% to GDP growth over the next decade while creating approximately 1.5 million jobs annually.`,
    source: 'Wall Street Journal',
    sourceUrl: 'https://www.wsj.com',
    category: 'politics',
    timestamp: '5 hours ago',
    image: 'https://images.unsplash.com/photo-1569025743873-ea3a9ber596w?w=800',
    sentiment: 'bullish',
    tickers: ['CAT', 'URI', 'VMC'],
    breaking: false,
    readTime: '4 min read',
    regions: ['US']
  },
  {
    id: 5,
    title: 'S&P 500 Closes at Record High as Tech Rally Extends',
    summary: 'The S&P 500 reached a new all-time high, led by technology stocks as investors bet on continued AI-driven growth.',
    content: `The S&P 500 closed at a record high Thursday, with technology stocks leading the advance as investors remained optimistic about artificial intelligence growth.

The benchmark index gained 1.2% to close at 5,234, surpassing its previous record set in January. The Nasdaq Composite rose 1.8%, while the Dow Jones Industrial Average added 0.7%.

Nvidia surged 5% after announcing expanded partnerships with major cloud providers. The chipmaker's market cap now exceeds $2 trillion, making it the third most valuable U.S. company.

"AI is not just a theme anymore, it's a fundamental driver of corporate earnings," said portfolio manager Lisa Wong. "Companies across sectors are seeing productivity gains."

Market breadth improved, with 380 S&P 500 components advancing. The equal-weight S&P 500 also hit a record, suggesting the rally is broadening beyond mega-cap tech.

Volume was above average at 11.2 billion shares, indicating strong conviction behind the move.`,
    source: 'CNBC',
    sourceUrl: 'https://www.cnbc.com',
    category: 'markets',
    timestamp: '6 hours ago',
    image: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800',
    sentiment: 'bullish',
    tickers: ['SPY', 'NVDA', 'MSFT'],
    breaking: false,
    readTime: '3 min read',
    regions: ['US', 'GLOBAL']
  },
  {
    id: 6,
    title: 'Ethereum ETFs See Record Inflows Ahead of Network Upgrade',
    summary: 'Ethereum exchange-traded funds attracted over $500 million in a single day as investors position ahead of the upcoming protocol upgrade.',
    content: `Ethereum ETFs recorded their largest single-day inflow since launch, attracting $523 million as investors anticipate the network's upcoming scaling upgrade.

The inflows were led by BlackRock's iShares Ethereum Trust, which added $287 million, followed by Fidelity's Ethereum Fund with $156 million. Total ETF assets now exceed $12 billion.

The surge comes ahead of Ethereum's "Pectra" upgrade, scheduled for Q1 2025, which promises to significantly improve transaction throughput and reduce fees.

"Ethereum is positioning itself as the backbone of decentralized finance," said crypto strategist David Park. "The upgrade could be a major catalyst."

ETH prices rose 6% to $4,200, outperforming Bitcoin on the day. The ETH/BTC ratio has been climbing, suggesting rotation into the second-largest cryptocurrency.

DeFi activity on Ethereum has also picked up, with total value locked rising to $65 billion, approaching previous cycle highs.`,
    source: 'The Block',
    sourceUrl: 'https://www.theblock.co',
    category: 'crypto',
    timestamp: '7 hours ago',
    image: 'https://images.unsplash.com/photo-1622630998477-20aa696ecb05?w=800',
    sentiment: 'bullish',
    tickers: ['ETH', 'ETHA'],
    breaking: false,
    readTime: '3 min read',
    regions: ['GLOBAL', 'US', 'EU']
  },
  {
    id: 7,
    title: 'Oil Prices Drop 4% on Surprise Inventory Build',
    summary: 'Crude oil fell sharply after U.S. inventory data showed an unexpected increase, raising concerns about demand weakness.',
    content: `Oil prices tumbled 4% Thursday after government data revealed a surprise build in U.S. crude inventories, intensifying concerns about global demand.

West Texas Intermediate crude fell to $72.30 per barrel, while Brent crude dropped to $77.15. The decline erased gains from earlier in the week.

The Energy Information Administration reported crude stockpiles rose by 5.8 million barrels, far exceeding the 1.2 million barrel draw analysts had expected.

"This is a bearish surprise that suggests demand may be softening," said energy analyst Robert Kim. "Refineries are also entering maintenance season, which typically reduces crude demand."

OPEC+ has signaled it may extend production cuts into 2025 to support prices. The cartel meets next month to decide on output policy.

Energy stocks fell with oil prices. Exxon Mobil dropped 2.3%, while Chevron lost 2.1%. The Energy Select Sector SPDR Fund (XLE) declined 2.5%.`,
    source: 'Reuters',
    sourceUrl: 'https://www.reuters.com',
    category: 'markets',
    timestamp: '8 hours ago',
    image: 'https://images.unsplash.com/photo-1513828583688-c52646db42da?w=800',
    sentiment: 'bearish',
    tickers: ['XOM', 'CVX', 'XLE'],
    breaking: false,
    readTime: '3 min read',
    regions: ['US', 'GLOBAL', 'EU']
  },
  {
    id: 8,
    title: 'Tesla Unveils Next-Generation Robotaxi Platform',
    summary: 'Tesla revealed its autonomous robotaxi design, targeting commercial launch in 2025 with plans to disrupt the ride-sharing industry.',
    content: `Tesla unveiled its next-generation robotaxi platform Thursday, showcasing a purpose-built autonomous vehicle designed for ride-sharing services.

The "Cybercab" features a futuristic design with butterfly doors and no steering wheel or pedals, reflecting Tesla's confidence in its Full Self-Driving technology.

CEO Elon Musk announced plans to begin commercial robotaxi operations in select markets by late 2025, pending regulatory approval.

"This will be the most cost-effective form of transportation ever created," Musk said at the unveiling event. "The cost per mile will be lower than public transit."

Tesla expects the Cybercab to cost under $30,000 to produce at scale, significantly less than current Model 3 prices. The company plans to operate its own fleet while also allowing owners to add their vehicles to the network.

Shares rose 8% on the announcement. Analysts see the robotaxi as a potential game-changer but note regulatory hurdles remain significant.`,
    source: 'Electrek',
    sourceUrl: 'https://electrek.co',
    category: 'markets',
    timestamp: '9 hours ago',
    image: 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=800',
    sentiment: 'bullish',
    tickers: ['TSLA', 'UBER', 'LYFT'],
    breaking: false,
    readTime: '4 min read',
    regions: ['US', 'GLOBAL']
  },
  {
    id: 9,
    title: 'ECB Holds Rates Steady, Signals Caution on Inflation',
    summary: 'The European Central Bank kept interest rates unchanged, warning that inflation remains sticky despite economic slowdown.',
    content: `The European Central Bank held interest rates steady at 4.0% Thursday, striking a cautious tone on inflation even as the eurozone economy shows signs of weakness.

ECB President Christine Lagarde emphasized that while inflation has moderated, it remains above the 2% target and could prove persistent.

European markets were mixed on the news, with the DAX falling 0.3% while the CAC 40 gained 0.2%.`,
    source: 'Financial Times',
    sourceUrl: 'https://www.ft.com',
    category: 'economy',
    timestamp: '10 hours ago',
    image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800',
    sentiment: 'neutral',
    tickers: ['EWG', 'EWQ', 'VGK'],
    breaking: false,
    readTime: '3 min read',
    regions: ['EU', 'DE', 'FR', 'GLOBAL']
  },
  {
    id: 10,
    title: 'Nikkei 225 Hits Record High on BOJ Policy Clarity',
    summary: 'Japanese stocks surged to all-time highs after the Bank of Japan provided clearer guidance on its monetary policy normalization.',
    content: `The Nikkei 225 index surged 2.5% to a record high Thursday after the Bank of Japan signaled a gradual approach to monetary policy normalization.

Japanese equities have been on a tear, with the benchmark index up over 30% year-to-date. Foreign investors have poured billions into Japanese stocks.

The yen strengthened slightly against the dollar, supporting exporters' earnings outlook.`,
    source: 'Nikkei Asia',
    sourceUrl: 'https://asia.nikkei.com',
    category: 'markets',
    timestamp: '11 hours ago',
    image: 'https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800',
    sentiment: 'bullish',
    tickers: ['EWJ', 'DXJ'],
    breaking: false,
    readTime: '2 min read',
    regions: ['JP', 'GLOBAL']
  },
  {
    id: 11,
    title: 'China Tech Giants Rally on Stimulus Optimism',
    summary: 'Chinese technology stocks surged as Beijing hints at additional economic stimulus measures to support growth.',
    content: `Chinese technology stocks rallied sharply Thursday as investors bet on additional government stimulus measures.

Alibaba surged 8%, while Tencent gained 6%. The Hang Seng Tech Index rose 5.2% on the day.

Beijing has signaled support for the property sector and consumer spending, boosting sentiment toward Chinese equities.`,
    source: 'South China Morning Post',
    sourceUrl: 'https://www.scmp.com',
    category: 'markets',
    timestamp: '12 hours ago',
    image: 'https://images.unsplash.com/photo-1474631245212-32dc3c8310c6?w=800',
    sentiment: 'bullish',
    tickers: ['BABA', 'TCEHY', 'JD'],
    breaking: false,
    readTime: '2 min read',
    regions: ['CN', 'HK', 'GLOBAL']
  },
];

function NewsPage() {
  const { t, preferences } = useUserPreferences();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookmarked, setBookmarked] = useState(() => {
    const saved = localStorage.getItem('elemental-news-bookmarks');
    return saved ? JSON.parse(saved) : [];
  });
  const [refreshing, setRefreshing] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Save bookmarks to localStorage
  useEffect(() => {
    localStorage.setItem('elemental-news-bookmarks', JSON.stringify(bookmarked));
  }, [bookmarked]);

  // Categories
  const categories = [
    { id: 'all', label: t('allNews') || 'All News', icon: <Newspaper size={14} /> },
    { id: 'markets', label: t('markets'), icon: <TrendingUp size={14} /> },
    { id: 'crypto', label: t('crypto'), icon: <Bitcoin size={14} /> },
    { id: 'economy', label: t('economy') || 'Economy', icon: <Landmark size={14} /> },
    { id: 'earnings', label: t('earnings') || 'Earnings', icon: <DollarSign size={14} /> },
    { id: 'politics', label: t('policy') || 'Policy', icon: <Building2 size={14} /> },
  ];

  // Filter news
  const filteredNews = mockNews.filter(article => {
    if (activeCategory === 'saved') {
      const isBookmarked = bookmarked.includes(article.id);
      const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.summary.toLowerCase().includes(searchQuery.toLowerCase());
      return isBookmarked && matchesSearch;
    }
    
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRegion = preferences.region === 'GLOBAL' || 
                         article.regions?.includes(preferences.region) ||
                         article.regions?.includes('GLOBAL');
    return matchesCategory && matchesSearch && matchesRegion;
  });

  // Set selected article
  useEffect(() => {
    if (!selectedArticle || !filteredNews.find(a => a.id === selectedArticle?.id)) {
      setSelectedArticle(filteredNews[0] || null);
    }
  }, [filteredNews, activeCategory, preferences.region]);

  // Breaking news
  const breakingNews = mockNews.filter(article => {
    const matchesRegion = preferences.region === 'GLOBAL' || 
                         article.regions?.includes(preferences.region) ||
                         article.regions?.includes('GLOBAL');
    return article.breaking && matchesRegion;
  });

  // Latest news
  const latestNews = filteredNews.filter(article => article.id !== selectedArticle?.id).slice(0, 6);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const toggleBookmark = (articleId) => {
    setBookmarked(prev => 
      prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  const handleShare = async (article) => {
    if (isSharing) return;
    setIsSharing(true);
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: article.title,
          text: article.summary,
          url: article.sourceUrl,
        });
      } else {
        await navigator.clipboard.writeText(article.sourceUrl);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.log('Share cancelled or failed');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const getSentimentColor = (sentiment) => {
    switch(sentiment) {
      case 'bullish': return '#10b981';
      case 'bearish': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Helper to check if article is saved
  const isSaved = (articleId) => bookmarked.includes(articleId);

  return (
    <div className="news-page">
      {/* Header */}
      <div className="news-header">
        <div className="news-title-section">
          <h1>{t('news')}</h1>
          <p>{t('latestNews')}</p>
        </div>
        <div className="news-header-actions">
          <div className="news-search">
            <Search size={14} />
            <input
              type="text"
              placeholder={t('searchNews')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button 
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Breaking News Ticker */}
      {breakingNews.length > 0 && activeCategory !== 'saved' && (
        <div className="breaking-ticker">
          <div className="breaking-label">
            <AlertCircle size={14} />
            {t('breaking')}
          </div>
          <div className="breaking-track">
            {breakingNews.map((article, idx) => (
              <span 
                key={article.id} 
                className="breaking-item"
                onClick={() => setSelectedArticle(article)}
              >
                {article.title}
                {idx < breakingNews.length - 1 && <span className="breaking-dot">•</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="news-categories">
        <div className="category-tabs-main">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>
        
        <div className="category-divider" />
        
        <button
          className={`category-btn saved-btn ${activeCategory === 'saved' ? 'active' : ''}`}
          onClick={() => setActiveCategory('saved')}
        >
          <BookmarkCheck size={14} />
          {t('saved') || 'Saved'}
          {bookmarked.length > 0 && (
            <span className="category-count">{bookmarked.length}</span>
          )}
        </button>
      </div>

      {/* Main Content Grid */}
      <div className="news-content">
        {/* Selected Article - Main View */}
        <div className="article-main">
          {selectedArticle ? (
            <>
              <div className="article-image">
                <img src={selectedArticle.image} alt={selectedArticle.title} />
                <div className="article-category-badge">{selectedArticle.category}</div>
                {isSaved(selectedArticle.id) && (
                  <div className="article-saved-badge">
                    <Bookmark size={12} fill="currentColor" />
                    Saved
                  </div>
                )}
              </div>
              
              <div className="article-header">
                <div className="article-meta">
                  <span className="article-source">
                    <Globe size={12} />
                    {selectedArticle.source}
                  </span>
                  <span className="article-time">
                    <Clock size={12} />
                    {selectedArticle.timestamp}
                  </span>
                  <span className="article-read-time">{selectedArticle.readTime}</span>
                  <span 
                    className="article-sentiment"
                    style={{ color: getSentimentColor(selectedArticle.sentiment) }}
                  >
                    {selectedArticle.sentiment === 'bullish' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {selectedArticle.sentiment}
                  </span>
                </div>
                
                <h2 className="article-title">{selectedArticle.title}</h2>
                
                <div className="article-tickers">
                  {selectedArticle.tickers.map(ticker => (
                    <span key={ticker} className="ticker-tag">${ticker}</span>
                  ))}
                </div>
              </div>

              <div className="article-content">
                {selectedArticle.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>

              <div className="article-actions">
                <a 
                  href={selectedArticle.sourceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="source-link"
                >
                  <ExternalLink size={14} />
                  Read on {selectedArticle.source}
                </a>
                <div className="article-buttons">
                  <button 
                    className={`action-btn ${isSaved(selectedArticle.id) ? 'active' : ''}`}
                    onClick={() => toggleBookmark(selectedArticle.id)}
                    title={isSaved(selectedArticle.id) ? 'Remove from saved' : 'Save article'}
                  >
                    <Bookmark size={16} fill={isSaved(selectedArticle.id) ? 'currentColor' : 'none'} />
                  </button>
                  <button 
                    className={`action-btn ${isSharing ? 'disabled' : ''}`}
                    onClick={() => handleShare(selectedArticle)}
                    disabled={isSharing}
                    title="Share article"
                  >
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <BookmarkCheck size={48} />
              <h3>No saved articles</h3>
              <p>Articles you bookmark will appear here</p>
              <button className="browse-btn" onClick={() => setActiveCategory('all')}>
                Browse News
              </button>
            </div>
          )}
        </div>

        {/* Other News - Middle Column */}
        <div className="news-list">
          <div className="news-list-header">
            <h3>{activeCategory === 'saved' ? (t('saved') || 'Saved') : (t('allNews') || 'All News')}</h3>
            <span className="news-count">{filteredNews.length}</span>
          </div>
          <div className="news-list-items">
            {filteredNews.length > 0 ? (
              filteredNews.map(article => (
                <div 
                  key={article.id}
                  className={`news-list-item ${selectedArticle?.id === article.id ? 'active' : ''}`}
                  onClick={() => setSelectedArticle(article)}
                >
                  <div className="news-item-badges">
                    {article.breaking && <span className="mini-breaking">{t('breaking')}</span>}
                    {isSaved(article.id) && activeCategory !== 'saved' && (
                      <span className="mini-saved" title="Saved">
                        <Bookmark size={10} fill="currentColor" />
                      </span>
                    )}
                  </div>
                  <h4>{article.title}</h4>
                  <div className="news-item-meta">
                    <span className="news-item-source">{article.source}</span>
                    <span className="news-item-time">{article.timestamp}</span>
                  </div>
                  <div className="news-item-tickers">
                    {article.tickers.slice(0, 2).map(ticker => (
                      <span key={ticker} className="mini-ticker">${ticker}</span>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-list">
                <BookmarkCheck size={24} />
                <p>{activeCategory === 'saved' ? 'No saved articles yet' : 'No articles found'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Latest News - Right Sidebar */}
        <div className="news-sidebar">
          <div className="sidebar-section">
            <div className="sidebar-header">
              <Flame size={14} />
              <h3>{t('trending')}</h3>
            </div>
            <div className="trending-list">
              {(activeCategory === 'saved' ? mockNews.slice(0, 4) : latestNews.slice(0, 4)).map((article, idx) => (
                <div 
                  key={article.id}
                  className={`trending-item ${isSaved(article.id) ? 'is-saved' : ''}`}
                  onClick={() => {
                    if (activeCategory === 'saved') setActiveCategory('all');
                    setSelectedArticle(article);
                  }}
                >
                  <span className="trending-rank">{idx + 1}</span>
                  <div className="trending-content">
                    <p>{article.title}</p>
                    <div className="trending-meta">
                      <span className="trending-source">{article.source}</span>
                      {isSaved(article.id) && (
                        <Bookmark size={10} fill="currentColor" className="trending-saved-icon" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <div className="sidebar-header">
              <Clock size={14} />
              <h3>{t('latestNews')}</h3>
            </div>
            <div className="latest-list">
              {(activeCategory === 'saved' ? mockNews.slice(0, 6) : latestNews).map(article => (
                <div 
                  key={article.id}
                  className={`latest-item ${isSaved(article.id) ? 'is-saved' : ''}`}
                  onClick={() => {
                    if (activeCategory === 'saved') setActiveCategory('all');
                    setSelectedArticle(article);
                  }}
                >
                  <div className="latest-time">
                    {article.timestamp}
                    {isSaved(article.id) && (
                      <Bookmark size={10} fill="currentColor" className="latest-saved-icon" />
                    )}
                  </div>
                  <p>{article.title}</p>
                  <div className="latest-meta">
                    <span 
                      className="latest-sentiment"
                      style={{ color: getSentimentColor(article.sentiment) }}
                    >
                      {article.sentiment === 'bullish' ? '↑' : article.sentiment === 'bearish' ? '↓' : '→'}
                    </span>
                    <span className="latest-source">{article.source}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewsPage;